import { LucideIcon } from "lucide-react";

interface HomepageTileProps {
  label: string;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  backgroundImage?: string;
  onClick: () => void;
  glowColor?: "primary" | "secondary" | "green" | "blue";
  size?: "xl" | "lg" | "md";
  animationDelay?: number;
  hideTitle?: boolean;
  logoPosition?: "center" | "top";
}

const HomepageTile = ({
  label,
  title,
  subtitle,
  icon: Icon,
  backgroundImage,
  onClick,
  glowColor = "primary",
  size = "md",
  animationDelay = 0,
  hideTitle = false,
  logoPosition = "top",
}: HomepageTileProps) => {
  const sizeClasses = {
    xl: "h-48 md:h-56",
    lg: "h-40 md:h-48",
    md: "h-28 md:h-36",
  };

  const titleSizes = {
    xl: "text-3xl md:text-5xl",
    lg: "text-2xl md:text-4xl",
    md: "text-sm sm:text-base md:text-lg lg:text-lg xl:text-xl",
  };

  const iconSizes = {
    xl: "h-8 w-8 md:h-10 md:w-10",
    lg: "h-6 w-6 md:h-8 md:w-8",
    md: "h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-8 lg:w-8 xl:h-9 xl:w-9",
  };

  const glowStyles = {
    primary: {
      glow: "shadow-[0_0_50px_hsl(var(--primary)/0.2)]",
      hoverGlow: "hover:shadow-[0_0_80px_hsl(var(--primary)/0.4)]",
      rimColor: "border-primary/20",
      hoverRim: "hover:border-primary/60",
      titleColor: "text-primary",
      shineColor: "from-primary/0 via-primary/30 to-primary/0",
      bottomGlow: "via-primary",
      hoverBorderColor: "hsl(var(--primary) / 0.3)",
    },
    secondary: {
      glow: "shadow-[0_0_50px_hsl(var(--secondary)/0.2)]",
      hoverGlow: "hover:shadow-[0_0_80px_hsl(var(--secondary)/0.4)]",
      rimColor: "border-secondary/20",
      hoverRim: "hover:border-secondary/60",
      titleColor: "text-secondary",
      shineColor: "from-secondary/0 via-secondary/30 to-secondary/0",
      bottomGlow: "via-secondary",
      hoverBorderColor: "hsl(var(--secondary) / 0.3)",
    },
    green: {
      glow: "shadow-[0_0_50px_rgba(34,197,94,0.2)]",
      hoverGlow: "hover:shadow-[0_0_80px_rgba(34,197,94,0.4)]",
      rimColor: "border-green-500/20",
      hoverRim: "hover:border-green-500/60",
      titleColor: "text-green-500",
      shineColor: "from-green-500/0 via-green-500/30 to-green-500/0",
      bottomGlow: "via-green-500",
      hoverBorderColor: "rgba(34, 197, 94, 0.3)",
    },
    blue: {
      glow: "shadow-[0_0_50px_rgba(59,130,246,0.2)]",
      hoverGlow: "hover:shadow-[0_0_80px_rgba(59,130,246,0.4)]",
      rimColor: "border-blue-500/20",
      hoverRim: "hover:border-blue-500/60",
      titleColor: "text-blue-500",
      shineColor: "from-blue-500/0 via-blue-500/30 to-blue-500/0",
      bottomGlow: "via-blue-500",
      hoverBorderColor: "rgba(59, 130, 246, 0.3)",
    },
  };

  const styles = glowStyles[glowColor];

  return (
    <button
      onClick={onClick}
      className={`
        group relative overflow-hidden rounded-2xl md:rounded-3xl
        ${sizeClasses[size]}
        w-full cursor-pointer
        transition-all duration-500 ease-out
        ${styles.glow} ${styles.hoverGlow}
        border ${styles.rimColor} ${styles.hoverRim}
        hover:scale-[1.02] active:scale-[0.98]
        opacity-0 animate-fade-in-up
      `}
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: 'forwards' }}
    >
      {/* Background image or gradient */}
      {backgroundImage ? (
        <>
          <div 
            className={`absolute bg-no-repeat transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 ${
              logoPosition === "center" 
                ? "inset-0 bg-center bg-[length:60%_auto] sm:bg-[length:55%_auto] md:bg-[length:50%_auto] lg:bg-[length:45%_auto]" 
                : "inset-2 sm:inset-3 md:inset-4 lg:inset-6 bg-center bg-[length:100%_auto] sm:bg-[length:95%_auto] md:bg-[length:90%_auto] lg:bg-[length:65%_auto]"
            }`}
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
          {/* Background fill behind logo */}
          <div className="absolute inset-0 bg-card -z-10" />
          {/* Gradient overlay for subtitle readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent group-hover:from-black/70 transition-all duration-500" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-card via-background to-card/80 group-hover:from-card/90 transition-all duration-500" />
      )}

      {/* Shine sweep effect */}
      <div 
        className={`
          absolute inset-0 -translate-x-full group-hover:translate-x-full
          bg-gradient-to-r ${styles.shineColor}
          transition-transform duration-700 ease-out
          skew-x-12
        `}
      />
      
      {/* Rim lighting with animated border */}
      <div 
        className={`
          absolute inset-0 rounded-2xl md:rounded-3xl 
          border-2 border-transparent
          transition-all duration-500
        `}
        style={{
          background: `linear-gradient(var(--background), var(--background)) padding-box, 
                       linear-gradient(135deg, ${styles.hoverBorderColor}, transparent, ${styles.hoverBorderColor}) border-box`
        }}
      />

      {/* Top-left label */}
      <span className={`absolute top-2 left-2 md:top-4 md:left-4 text-[8px] md:text-[10px] font-bold uppercase tracking-[0.15em] md:tracking-[0.2em] text-white/70 group-hover:text-white/90 transition-all duration-300 group-hover:tracking-[0.25em]`}>
        {label}
      </span>

      {/* Content - positioned at bottom for image tiles, center for icon tiles */}
      <div className={`absolute inset-0 flex flex-col ${backgroundImage ? 'justify-end pb-4 md:pb-6' : 'justify-center items-center'} px-3 md:px-6`}>
        <div className={`flex items-center gap-1.5 md:gap-3 ${backgroundImage ? '' : 'flex-col'} transition-transform duration-500 group-hover:-translate-y-1`}>
          {Icon && (
            <div className={`${styles.titleColor} opacity-90 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110`}>
              <Icon className={iconSizes[size]} strokeWidth={2} />
            </div>
          )}
          {!hideTitle && (
            <h3 
              className={`
                ${titleSizes[size]} font-bold tracking-tight text-center
                ${backgroundImage ? 'text-white' : styles.titleColor}
                transition-all duration-500
                group-hover:tracking-wide
                ${backgroundImage 
                  ? 'drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)]' 
                  : `drop-shadow-[0_0_15px_hsl(var(--${glowColor})/0.5)]`}
              `}
            >
              {title}
            </h3>
          )}
        </div>
        
        {subtitle && (
          <p className={`
            mt-1.5 md:mt-2 text-[11px] md:text-sm 
            text-white/70 group-hover:text-white/90
            leading-relaxed max-w-[95%]
            transition-all duration-500 group-hover:-translate-y-0.5
          `}>
            {subtitle}
          </p>
        )}
      </div>

      {/* Bottom glow line - enhanced */}
      <div 
        className={`
          absolute bottom-0 left-1/2 -translate-x-1/2 
          w-0 group-hover:w-4/5 
          h-[3px]
          bg-gradient-to-r from-transparent ${styles.bottomGlow} to-transparent
          transition-all duration-500 ease-out
          blur-[1px]
        `}
      />
      
      {/* Corner accent */}
      <div 
        className={`
          absolute bottom-0 right-0 w-20 h-20
          opacity-0 group-hover:opacity-100
          transition-opacity duration-500
          rounded-tl-full
        `}
        style={{
          background: `linear-gradient(to top left, ${styles.hoverBorderColor}, transparent)`
        }}
      />
    </button>
  );
};

export default HomepageTile;
